import {
    InteractionRequiredAuthError,
    PublicClientApplication,
    type AccountInfo,
    type AuthenticationResult
} from "@azure/msal-browser";
import {
    slackMrkdwnFragmentToPreviewHtml,
    slackMrkdwnToDeliveryRecordPreviewHtml
} from "../slackPreview";
import { attr, resolveEmbedConfig, type InboxEmbedConfig } from "./inboxEmbedConfig";
import { mount } from "./msgautoInbox";

export function currentInboxScript(): HTMLScriptElement | null {
    if (document.currentScript instanceof HTMLScriptElement) {
        return document.currentScript;
    }
    const bySrc = document.querySelectorAll<HTMLScriptElement>('script[src*="msgauto-inbox"]');
    if (bySrc.length) {
        return bySrc[bySrc.length - 1] || null;
    }
    const byAttr = document.querySelectorAll<HTMLScriptElement>(
        "script[data-client-id], script[data-api], script[data-target='#gmone-messages']"
    );
    return byAttr[byAttr.length - 1] || null;
}

function normalizeEmojiMap(data: unknown): Record<string, string> {
    const map: Record<string, string> = {};
    if (!data || typeof data !== "object") {
        return map;
    }

    const add = (rawKey: string, rawVal: unknown): void => {
        const key = String(rawKey || "").trim().replace(/^:|:$/g, "");
        const value = typeof rawVal === "string" ? rawVal.trim() : "";
        if (key && value) {
            map[key] = value;
        }
    };

    if (Array.isArray(data)) {
        for (const row of data) {
            if (!row || typeof row !== "object") {
                continue;
            }
            const rec = row as Record<string, unknown>;
            add(String(rec.Key ?? rec.key ?? rec.Name ?? rec.name ?? ""), rec.Value ?? rec.value ?? rec.Url ?? rec.url);
        }
        return map;
    }

    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
        add(key, value);
    }
    return map;
}

async function loadEmojiMap(apiBase: string, token: string, cookie: boolean): Promise<Record<string, string>> {
    try {
        const headers: Record<string, string> = { Accept: "application/json" };
        if (!cookie) {
            headers.Authorization = `Bearer ${token}`;
        }
        const url = cookie ? `${apiBase}/Inbox/Emojis` : `${apiBase}/v2.0/Notifications/emojis`;
        const response = await fetch(url, {
            headers,
            credentials: cookie ? "include" : "omit"
        });
        if (response.ok) {
            return normalizeEmojiMap(await response.json());
        }
    } catch {
        /* preview still works with unicode emoji */
    }
    return {};
}

function toShortcode(raw: string): string {
    const name = raw.trim().replace(/^:+/, "").replace(/:+$/, "");
    return name ? `:${name}:` : "";
}

/** Inject a Messages FAB unless the host already has a trigger (or homepage Messages control). */
export function ensureInboxTrigger(script: HTMLScriptElement | null): HTMLElement {
    const targetSel = attr(script, "data-target");
    if (targetSel) {
        const existing = document.querySelector<HTMLElement>(targetSel);
        if (existing) {
            return existing;
        }
    }

    const homepage = document.getElementById("msgauto-inbox-open");
    if (homepage) {
        return homepage as HTMLElement;
    }

    const existingFab = document.getElementById("msgauto-inbox-fab");
    if (existingFab instanceof HTMLElement) {
        return existingFab;
    }

    const button = document.createElement("button");
    button.id = "msgauto-inbox-fab";
    button.type = "button";
    button.className = "msgauto-inbox-fab";
    button.setAttribute("aria-label", "AskGM Hub");
    button.textContent = "AskGM Hub";
    document.body.appendChild(button);
    return button;
}

export type InboxTokenSession = {
    getToken: (interactive?: boolean) => Promise<string>;
    /** False when loginRedirect is sending the browser to Entra (page will unload). */
    whenSignedIn: Promise<boolean>;
};

/**
 * MSAL public-client tokens for the MsgAuto API.
 * On load: complete a redirect return, then ssoSilent, then loginRedirect so Entra
 * runs before the inbox mounts. After that, silent acquire; popup only on a later click
 * if the token needs interaction again.
 */
export function createInboxTokenFactory(config: InboxEmbedConfig): InboxTokenSession {
    const scopes = [config.scope];
    const pca = new PublicClientApplication({
        auth: {
            clientId: config.clientId,
            authority: `https://login.microsoftonline.com/${config.tenantId}`,
            redirectUri: window.location.origin
        },
        cache: { cacheLocation: "sessionStorage" }
    });

    const account = (): AccountInfo | null => pca.getAllAccounts()[0] || null;
    const fromResult = (result: AuthenticationResult): string => result.accessToken;

    const whenSignedIn = pca
        .initialize()
        .then(() => pca.handleRedirectPromise())
        .then(async () => {
            if (account()) {
                return true;
            }
            try {
                await pca.ssoSilent({ scopes });
            } catch {
                /* no existing Entra session for this SPA — fall through to redirect */
            }
            if (account()) {
                return true;
            }
            await pca.loginRedirect({ scopes, redirectStartPage: window.location.href });
            return false;
        });

    const acquire = (current: AccountInfo | null, interactive: boolean): Promise<string> => {
        if (current) {
            return pca
                .acquireTokenSilent({ scopes, account: current })
                .then(fromResult)
                .catch((error: unknown) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        if (!interactive) {
                            throw error;
                        }
                        return pca.acquireTokenPopup({ scopes }).then(fromResult);
                    }
                    throw error;
                });
        }
        if (!interactive) {
            throw new InteractionRequiredAuthError("interaction_required");
        }
        return pca.loginRedirect({ scopes, redirectStartPage: window.location.href }).then(() => {
            throw new InteractionRequiredAuthError("interaction_required");
        });
    };

    const getToken = (interactive = true): Promise<string> =>
        whenSignedIn.then((signedIn) => {
            if (!signedIn) {
                throw new InteractionRequiredAuthError("interaction_required");
            }
            return acquire(account(), interactive);
        });

    return { getToken, whenSignedIn };
}

function isUsableSpaClientId(clientId: string): boolean {
    const id = clientId.trim();
    if (!id) {
        return false;
    }
    return !/^YOUR_SPA_PUBLIC_CLIENT_ID$/i.test(id);
}

export function bootInboxEmbed(script: HTMLScriptElement | null = currentInboxScript()): void {
    const config = resolveEmbedConfig(script);
    const demo = /^(1|true|yes)$/i.test(attr(script, "data-demo"));
    const cookieAuth = /^(cookie|sso)$/i.test(attr(script, "data-auth"));

    if (!demo && !cookieAuth && !isUsableSpaClientId(config.clientId)) {
        const message =
            "MsgAuto inbox: set data-client-id to the Entra SPA Application (client) ID. YOUR_SPA_PUBLIC_CLIENT_ID is a placeholder, not an app. Do not use the confidential Web app id.";
        console.error(message);
        const targetSel = attr(script, "data-target");
        const slot = targetSel ? document.querySelector(targetSel) : null;
        if (slot) {
            slot.innerHTML =
                `<div class="msgauto-inbox msgauto-inbox--card"><div class="msgauto-inbox__status msgauto-inbox__status--error">` +
                `<p>Sign-in is not configured.</p>` +
                `<p>Register an Entra <strong>SPA public client</strong> (no secret), add redirect URI <code>https://localhost:8081</code>, then put that app’s GUID in <code>data-client-id</code>.</p>` +
                `</div></div>`;
        }
        return;
    }

    const trigger = ensureInboxTrigger(script);
    if (trigger.id === "msgauto-inbox-open" && !attr(script, "data-force-embed")) {
        return;
    }

    const startInbox = (acquireToken: (interactive?: boolean) => Promise<string>): void => {
        const embedded = trigger.tagName !== "BUTTON" && trigger.tagName !== "A";
        const pageSizeAttr = Number.parseInt(attr(script, "data-page-size"), 10);
        const pageSize = pageSizeAttr > 0 ? pageSizeAttr : embedded ? 5 : 20;
        const pagerAttr = attr(script, "data-pager");
        const pager = pagerAttr ? /^(1|true|yes)$/i.test(pagerAttr) : embedded;
        const pollSeconds = Number.parseInt(attr(script, "data-poll"), 10);
        const pollMs = pollSeconds > 0 ? pollSeconds * 1000 : 0;
        let emojiMap: Record<string, string> = {};
        let emojisLoaded = false;

        const getToken = async (interactive = true): Promise<string> => {
            const token = await acquireToken(interactive);
            if (!emojisLoaded) {
                emojiMap = await loadEmojiMap(config.apiBase, token, cookieAuth);
                emojisLoaded = true;
            }
            return token;
        };

        mount(trigger, {
            apiBase: config.apiBase,
            getToken,
            authMode: cookieAuth ? "cookie" : "bearer",
            offcanvas: !embedded,
            fillViewport: embedded && !pager,
            hideHeader: embedded,
            pageSize,
            pager,
            pollMs,
            slackPreview: true,
            lazy: !embedded && !demo,
            authPrompt: embedded && !demo,
            formatBody: (raw, subject) => slackMrkdwnToDeliveryRecordPreviewHtml(raw, emojiMap, subject),
            formatAppEmoji: (raw, imageUrl) => {
                if (imageUrl && /^https?:\/\//i.test(imageUrl)) {
                    const src = imageUrl.replace(/"/g, "&quot;");
                    return `<img class="ma-ae-slack-emoji" src="${src}" alt="" />`;
                }
                const shortcode = toShortcode(raw);
                return shortcode ? slackMrkdwnFragmentToPreviewHtml(shortcode, emojiMap) : "";
            }
        });
    };

    if (demo) {
        startInbox(async () => "demo");
        return;
    }
    if (cookieAuth) {
        startInbox(async () => "");
        return;
    }

    const targetSel = attr(script, "data-target");
    const slot = targetSel ? document.querySelector(targetSel) : null;
    if (slot && !slot.querySelector(".msgauto-inbox")) {
        slot.innerHTML =
            `<div class="msgauto-inbox msgauto-inbox--card"><div class="msgauto-inbox__status">Signing in…</div></div>`;
    }

    const session = createInboxTokenFactory(config);
    void session.whenSignedIn
        .then((signedIn) => {
            if (!signedIn) {
                return;
            }
            startInbox(session.getToken);
        })
        .catch((error: unknown) => {
            console.error("MsgAuto inbox: Entra sign-in failed.", error);
            if (slot) {
                slot.innerHTML =
                    `<div class="msgauto-inbox msgauto-inbox--card"><div class="msgauto-inbox__status msgauto-inbox__status--error">` +
                    `<p>Sign-in failed. Refresh to try again.</p>` +
                    `</div></div>`;
            }
        });
}
